<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { useI18n } from '@/common/composables/vue-i18n.composable';
  import { totpCodeRule } from '@/common/validation/authRules';

  interface Props {
    pending?: boolean;
    error?: string;
    showBackButton?: boolean;
    showHeader?: boolean;
    title?: string;
    username?: string;
    submitButtonText?: string;
    cancelButtonText?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    pending: false,
    error: '',
    showBackButton: true,
    showHeader: true,
    title: '',
    username: '',
    submitButtonText: '',
    cancelButtonText: ''
  });

  const emit = defineEmits(['submit', 'cancel', 'helpRequested', 'input']);
  const { t } = useI18n();

  const formRef = ref();
  const form = ref({
    verificationCode: ''
  });

  const displayTitle = computed(() => props.title || t('Two-factor verification'));

  const isFormValid = computed(() => {
    if (!formRef.value?.fields) return false;
    const hasErrors = formRef.value.fields.some(field => field.validateState === 'error');
    const allValidated = formRef.value.fields.every(field => field.validateState === 'success');
    return !hasErrors && allValidated;
  });

  const rules = computed(() => ({
    verificationCode: totpCodeRule()
  }));

  const handleSubmit = async () => {
    if (props.pending) {
      return;
    }

    try {
      const isValid = await formRef.value?.validate();
      if (!isValid) {
        return;
      }

      emit('submit', form.value.verificationCode);
    } catch (err) {
      // Validation failed - errors are displayed inline by el-form-item
    }
  };

  const handleCancel = () => {
    emit('cancel');
  };

  const handleHelpRequested = () => {
    emit('helpRequested');
  };

  const clearErrors = () => {
    formRef.value?.clearValidate();
    emit('input');
  };

  const resetForm = () => {
    form.value.verificationCode = '';
    clearErrors();
  };

  defineExpose({
    resetForm,
    clearErrors,
    validate: () => formRef.value?.validate(),
    formRef
  });
</script>

<template>
<div data-component-name="totp-verification" class="totp-verification">
  <el-form data-component-name="el-form"
    ref="formRef"
    v-loading="pending"
    accept-charset="UTF-8"
    el-loading-background="fixed-white"
    :el-loading-opacity=".8"
    :el-loading-size="48"
    errors-in-holder
    :model="form"
    role="form"
    :rules="rules"
    @keyup.enter.native="handleSubmit"
    @submit.prevent="handleSubmit"
  >
    <template v-if="showHeader">
      <h2 class="header">
        {{ displayTitle }}
      </h2>

      <div
        v-if="username"
        class="username pb-24"
      >
        {{ username }}
      </div>
    </template>

    <el-form-item data-component-name="el-form-item"
      :error="error"
      prop="verificationCode"
    >
      <el-input data-component-name="el-input"
        v-model="form.verificationCode"
        autofocus
        data-testid="totp-code"
        :label="$t('Enter the 6-digit code from your app')"
        :maxlength="6"
        name="totp-code"
        required
        spellcheck="false"
        @input="clearErrors"
      />
    </el-form-item>

    <slot
      name="actions"
      :on-submit="handleSubmit"
      :on-cancel="handleCancel"
      :pending="pending"
      :is-valid="isFormValid"
    >
      <div class="default-actions">
        <el-button data-component-name="el-button"
          v-if="showBackButton"
          data-testid="cancel"
          fluid
          size="large"
          type="secondary"
          @click="handleCancel"
        >
          {{ cancelButtonText || $t('Back') }}
        </el-button>
        <el-button data-component-name="el-button"
          data-testid="submit"
          :disabled="pending || !isFormValid"
          fluid
          name="verify"
          native-type="submit"
          size="large"
          type="primary"
          @click="handleSubmit"
        >
          {{ submitButtonText || $t('Verify') }}
        </el-button>
      </div>
    </slot>

    <slot name="help-link">
      <el-button data-component-name="el-button"
        class="mt-24"
        fluid
        type="ghost"
        @click="handleHelpRequested"
      >
        {{ $t('Lost your authenticator app?') }}
      </el-button>
    </slot>
  </el-form>
</div>
</template>

<style lang="scss" scoped>
.totp-verification {
  position: relative;

  .header {
    font-size: $av-font-size-l;
    font-weight: $av-font-weight-normal;
    font-style: normal;
    font-stretch: normal;
    line-height: 32px;
    letter-spacing: normal;
    text-align: center;
    color: $av-fixed-primary;
    margin: 0 0 16px;
  }

  .username {
    font-size: $av-font-size-xs;
    text-align: center;
    color: $av-fixed-light;
  }

  .default-actions {
    display: flex;
    gap: 16px;
    width: 100%;
  }
}
</style>
